import React, { useEffect, useRef } from "react";
import {
  CreateUserPostModel,
  GeminiDomainCategory,
  ICreateUserPostModel,
  UserEmailCategoryPostModel,
} from "../../swagger-clients/s365-dashboard-v2-api-clients.service";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  Button,
  DialogContent,
  DialogActions,
  Label,
  Field,
  Input,
  Checkbox,
  dividerClassNames,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Title3,
  Subtitle1,
  Subtitle2,
} from "@fluentui/react-components";
import {
  CheckmarkRegular,
  CheckRegular,
  ChevronRightRegular,
  CopyRegular,
  Dismiss24Filled,
  MailRegular,
  PasswordRegular,
  PeopleRegular,
  PersonRegular,
  WarningRegular,
} from "@fluentui/react-icons";
import { GLOBAL_STYLES } from "../../styles";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import "./register-modal.scss";
import { processServerError } from "../../utils/helpers/error.helper";
import { getUsersClient } from "../../services/dashboard.service";
import { toast } from "react-toastify";
import { useAuth } from "react-oidc-context";
import { S365Button } from "../../components/s365-button/s365-button.component";
import { getCurrentWebUrl } from "../../App";

import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import 'react-phone-number-input/style.css';
import useReferrerTracker from "../../hooks/useReferrerTracker ";

type RegisterModalProps = {
  isOpened?: boolean;
  onClose: () => void;
};

type RegisterUserModel = Omit<
  ICreateUserPostModel,
  "accountConfirmationPage"
> & {
  privacyPolicyConsent: boolean;
  mailCategory?: GeminiDomainCategory;
  useCurrentMailAccepted?: boolean;
  hCaptchaVerified: boolean;
};

const formSchema = Yup.object().shape<
  Record<keyof RegisterUserModel, Yup.AnySchema>
>({
  firstName: Yup.string().required("First name is required."),
  lastName: Yup.string().required("Last name is required."),
  email: Yup.string()
    .required("Email is required.")
    .matches(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'Email is not in correct format.'
    ),
  // businessPhone: Yup.string()
  //   .required("Business phone is required.")
  //   .test(
  //     'is-valid-phone',
  //     'Business phone is not in correct format.',
  //     (value) => isValidPhoneNumber(value || '')
  //   ),
  businessPhone: Yup.string().notRequired(),
  password: Yup.string()
    .required("Password is required.")
    .min(8, "Password must be at least 8 characters long.") // Minimum length
    .matches(/[a-z]/, "Password must contain at least one lowercase letter.") // At least one lowercase letter
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter.") // At least one uppercase letter
    .matches(/\d/, "Password must contain at least one number.") // At least one number
    .matches(/[@$!%*?&]/, "Password must contain at least one special character."),
  privacyPolicyConsent: Yup.boolean()
    .required("Privacy policy consent is required.")
    .oneOf([true], "Privacy policy consent is required."),
  hCaptchaVerified: Yup.boolean()
    .required("Captcha is required.")
    .oneOf([true], "Captcha is required."),
  mailCategory: Yup.mixed<GeminiDomainCategory>(), //.required('Mail category is required'),
  useCurrentMailAccepted: Yup.boolean().when('mailCategory', {
    is: GeminiDomainCategory.Other,
    then: (schema) => schema
      .required('You must accept business email policy')
      .oneOf([true], 'You must accept business email policy'),
    otherwise: (schema) => schema.notRequired(),
  }),
  isDwsimRefferer: Yup.boolean().notRequired()
});

export const RegisterModal: React.FC<RegisterModalProps> = (props) => {
  const auth = useAuth();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isCheckingEmail, setIsCheckingEmail] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState(false);
  const [userPrincipalName, setUserPrincipalName] = React.useState<string>();

  const { getStoredReferrer } = useReferrerTracker({
    referrerParam: 'referrer',
    storageKey: 'referrer'
  });
  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    setValue,
    formState: { errors, isSubmitted },
    reset,
  } = useForm<RegisterUserModel>({ resolver: yupResolver(formSchema) as any });
  React.useEffect(() => {
    setOpen(!!props.isOpened);
  }, [props.isOpened]);
  const onModalClose = () => {
    setOpen(false);
    reset();
    if (props.onClose) {
      props.onClose();
    }
  };
  const providedEmail = watch("email");
  const useCurrentMailAccepted = watch("useCurrentMailAccepted");
  const mailCategory = watch("mailCategory");

  const onSubmit = async (data: RegisterUserModel) => {
    try {
      setIsLoading(true);
      console.log("onSubmit", data);
      const client = getUsersClient();
      const model = new CreateUserPostModel({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        businessPhone: data.businessPhone,
        isDwsimRefferer: getStoredReferrer()?.toUpperCase() == 'DWSIM' ? true : false
      });
      const createUserResp = await client.createUser(model);
      setUserPrincipalName(createUserResp.userPrincipalName!);
    } catch (error) {
      processServerError(
        error,
        setError,
        "An error occurred while registering user."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onLoginClick = () => {
    auth.signinRedirect({ redirect_uri: getCurrentWebUrl() + "/" });
  };

  const checkUserEmailCategory = async () => {
    try {
      if (!providedEmail || providedEmail.indexOf("@") == -1) {
        return;
      }
      setIsCheckingEmail(true);
      const client = getUsersClient();
      const model = new UserEmailCategoryPostModel({
        email: providedEmail
      });

      const categoryResp = await client.getUserEmailCategory(model);

      setValue("mailCategory", categoryResp.domainCategory ?? GeminiDomainCategory.Other);

    } catch (error) {
      processServerError(error, undefined, "An error occurred while checking email category.", false);
    } finally {
      setIsCheckingEmail(false);
    }
  }

  const onCopyUsernameClick = (ev) => {
    ev.preventDefault();
    navigator.clipboard.writeText(userPrincipalName);
    toast.success(`Username copied to clipboard.`);
  }





  return (
    <Dialog
      open={open}
      modalType="alert"
      inertTrapFocus
      onOpenChange={(event, data) => {
        setOpen(data.open);
        if (!data.open) {
          onModalClose();
        }
      }}
    >
      <DialogSurface style={{ maxWidth: "650px" }}>
        <form className="register-form-modal" noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogBody style={{ maxWidth: "inherit" }}>
            <DialogTitle
              action={
                <Button
                  disabled={isLoading}
                  appearance="transparent"
                  onClick={onModalClose}
                  icon={<Dismiss24Filled />}
                />
              }
            />
            {!isLoading && !userPrincipalName && (<DialogContent
              className={GLOBAL_STYLES.DIALOG_CONTENT_WITH_SPACING}
              style={{ paddingLeft: "100px", paddingRight: "100px" }}
            >

              <h2 style={{ marginTop: 0, marginBottom: "var(--spacingVerticalXXXL)", textAlign: 'center' }}>Sign up to Simulate 365</h2>
              <div className="input-form">
                <div style={{ display: "flex", gap: "var(--spacingHorizontalL)", width: "100%" }}>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", marginBottom: "var(--spacingVerticalL)" }}>
                    <Field
                      required
                      label="First name"
                      className={`${GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}`}
                      validationState={errors.firstName ? "error" : "none"}
                      validationMessage={
                        errors.firstName ? errors.firstName.message : undefined
                      }
                    >
                      <Input
                        id="firstName"
                        className="input-field"
                        {...register("firstName")}
                      //   contentBefore={<PersonRegular />}
                      />
                    </Field>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", marginBottom: "var(--spacingVerticalL)" }}>
                    <Field
                      label="Last name"
                      required
                      className={`${GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}`}
                      validationState={errors.lastName ? "error" : "none"}
                      validationMessage={
                        errors.lastName ? errors.lastName.message : undefined
                      }
                    >
                      <Input
                        id="lastName"
                        className="input-field"
                        {...register("lastName")}
                      //  contentBefore={<PersonRegular />}
                      />
                    </Field>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalL)" }}>
                  <Field
                    label="Business Email"
                    required
                    className={`${GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}`}
                    validationState={errors.email ? "error" : "none"}
                    validationMessage={
                      errors.email ? errors.email.message : undefined
                    }
                  >
                    <Input
                      id="email"
                      type="email"
                      className="input-field"
                      {...register("email")}
                      //   contentBefore={<MailRegular />}
                      onBlur={() => { checkUserEmailCategory(); }}
                      contentAfter={isCheckingEmail ? <Spinner size="tiny" /> : null}
                    />
                  </Field>
                </div>

                {mailCategory === GeminiDomainCategory.Other && <div style={{ marginBottom: "var(--spacingVerticalL)" }}>
                  <Field
                    validationState={
                      errors.useCurrentMailAccepted ? "error" : "none"
                    }
                    validationMessage={
                      errors.useCurrentMailAccepted
                        ? errors.useCurrentMailAccepted.message
                        : undefined
                    }
                  >
                    <Checkbox
                      {...register("useCurrentMailAccepted")}
                      label={
                        <small style={{ display: "block", lineHeight: "var(--lineHeightBase200)", color: "var(--colorStatusWarningForeground1)" }}>
                          I understand that by signing up with a <b><u>private email</u></b> I will not be able to access all Simulate 365 features and services.
                        </small>
                      }
                    />
                  </Field>
                </div>}

                {/* <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalL)" }}>
                  <Field
                    label="Business phone"
                    required
                    className={`${GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}`}
                    validationState={errors.businessPhone ? "error" : "none"}
                    validationMessage={
                      errors.businessPhone ? errors.businessPhone.message : undefined
                    }
                  >
                    <PhoneInput
                      international
                      placeholder="Enter phone number"
                      {...register("businessPhone")}
                      onChange={(value) => {
                        setValue("businessPhone", value, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        })
                      }}
                      defaultCountry="DE" // or "BA", "DE", etc.
                      className="phone-input-field"
                      countrySelectProps={{
                        className: 'react-phone-number-input__country-select',
                        optionClassName: 'react-phone-number-input__country-select-option',
                        dividerClassName: 'react-phone-number-input__country-select-divider'
                      }}
                      style={{
                        '--PhoneInputCountryFlag-height': '0.9em',
                        '--PhoneInputCountryFlag-borderColor': 'transparent',
                        '--PhoneInputCountrySelectArrow-color': 'currentColor',
                      }}
                    />
                  </Field>
                </div> */}

                <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "var(--spacingVerticalL)" }}>
                  <Field
                    label="Password"
                    required
                    className={`${GLOBAL_STYLES.INPUT_FIELD_FULL_WIDTH}`}
                    validationState={errors.password ? "error" : "none"}
                    validationMessage={
                      errors.password ? errors.password.message : undefined
                    }
                    hint={"Minimum length is 8 characters with at least one uppercase letter, lowercase letter, number and special character."}
                  >
                    <Input
                      id="password"
                      type="password"
                      className="input-field"
                      {...register("password")}
                    // contentBefore={<PasswordRegular />}                      
                    />
                  </Field>
                </div>

                <div style={{ marginBottom: "var(--spacingVerticalXL)" }}>
                  <Field
                    validationState={
                      errors.privacyPolicyConsent ? "error" : "none"
                    }
                    validationMessage={
                      errors.privacyPolicyConsent
                        ? errors.privacyPolicyConsent.message
                        : undefined
                    }
                  >
                    <Checkbox
                      {...register("privacyPolicyConsent")}
                      label={
                        <small style={{ display: "block", lineHeight: "var(--lineHeightBase200)" }}>

                          By signing up, you agree to the CGC Capital-Gain Consultants  <a
                             href="/privacy-policy/CGC-PrivacyPolicy.html"
                            target="_blank"
                          >
                            Privacy Policy
                          </a> and   <a
                            href="/eula/CGC-Cloud-EULA.html"
                            target="_blank"
                          >
                            Cloud End-User License Agreement (EULA)
                          </a>. You agree that we collect your details and that we may use the information provided to contact you about our products and events.
                          You may opt out from our communication at any time.

                        </small>
                      }
                    />
                  </Field>
                </div>

                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                  <Field
                    validationState={
                      errors.hCaptchaVerified ? "error" : "none"
                    }
                    validationMessage={
                      errors.hCaptchaVerified
                        ? errors.hCaptchaVerified.message
                        : undefined
                    }
                  >
                    <HCaptcha
                      sitekey="ca28c15d-d3b1-4075-97c8-7dbcfea0262e"
                      onVerify={(token) => {
                        // console.log("onVerify hcaptcha", token);
                        if (!!token)
                          setValue("hCaptchaVerified", true, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                      }}
                    />
                  </Field>
                </div>
              </div>




              {!isLoading && !userPrincipalName && <div style={{ display: "flex", justifyContent: "center" }}>
                <S365Button appearance="primary" type="submit" size="large" style={{ width: "300px" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ paddingBottom: "3px", paddingRight: "var(--spacingHorizontalS)" }}>Continue</span>
                    <ChevronRightRegular />
                  </div>

                </S365Button>
              </div>
              }
            </DialogContent>)}

            {!isLoading && !!userPrincipalName &&
              (<DialogContent
                className={GLOBAL_STYLES.DIALOG_CONTENT_WITH_SPACING}
                style={{ paddingLeft: "100px", paddingRight: "100px", paddingTop: 0 }}
              >

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "center",
                    alignItems: "center",
                  }}
                >
                  <h2 style={{ marginTop: 0, marginBottom: "var(--spacingVerticalXXXL)" }}>Thank you for signing up!</h2>
                  <span style={{ marginBottom: "var(--spacingVerticalXL)" }}>
                    <b>Sign in with your username</b>.
                  </span>
                  <Input
                    size="large"
                    style={{ width: "100%", marginBottom: "var(--spacingVerticalXL)" }}
                    input={{ style: { textAlign: "center", fontWeight: "bold" } }}
                    value={userPrincipalName} readOnly
                    contentAfter={<Button appearance="transparent" icon={<CopyRegular onClick={onCopyUsernameClick} />}></Button>}
                  />

                  <S365Button
                    size="large"
                    appearance="secondary"
                    style={{ width: "fit-content" }}
                    onClick={onLoginClick}
                  >
                    Continue to sign in
                  </S365Button>
                  {/* TP user story to remove this message: https://capitalgain.tpondemand.com/entity/33503-update-the-thank-you-for-signing
                   <MessageBar icon={<WarningRegular />} intent="warning" layout="multiline" style={{ marginTop: "20px", alignItems: "center", width: "100%" }}>
                    <MessageBarBody>
                      <span>We sent to your email an account confirmation link.<br />
                        Click on the confirmation link in the next 24 hours <br /> to keep your Simulate 365 account active.</span>
                    </MessageBarBody>
                  </MessageBar> */}
                </div>
              </DialogContent>)}

            {isLoading && <DialogContent
              className={GLOBAL_STYLES.DIALOG_CONTENT_WITH_SPACING}
              style={{ paddingLeft: "100px", paddingRight: "100px", paddingTop: 0 }}>
              <div>
                {!userPrincipalName && <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Spinner label={"Creating your account, please wait..."} />
                </div>}
              </div>
            </DialogContent>}

          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};
